import { Bot, Check, CircleDashed, Flame, ShieldCheck, Sparkles } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

// Conversation Intelligence presentation. AI-inferred facts must never look verified (decision 2):
// provenance is always shown as text + icon.

// ───────── ProvenanceBadge ─────────

export interface ProvenanceBadgeProps {
  /** true = confirmed by a system of record (calendar/CRM); false = inferred by AI from the chat. */
  verified: boolean;
  /** Compact form: "IA" / "Verificada". */
  compact?: boolean;
  className?: string;
}

export function ProvenanceBadge({ verified, compact, className }: ProvenanceBadgeProps) {
  return (
    <span
      data-verified={verified}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-0.5 text-helper font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
        verified ? 'border-success/25 bg-success-bg text-success' : 'border-border-control bg-surface-default text-text-secondary',
        className,
      )}
    >
      {verified ? <ShieldCheck aria-hidden /> : <Bot aria-hidden />}
      {verified ? (compact ? 'Verificada' : 'Verificada (calendario/CRM)') : compact ? 'IA' : 'Inferida de la conversación · no verificada'}
    </span>
  );
}

// ───────── AppointmentFunnel ─────────

export type FunnelState = 'none' | 'opportunity' | 'proposed' | 'accepted' | 'confirmed_from_conversation' | 'confirmed_external';

const STEPS: Array<{ key: Exclude<FunnelState, 'none' | 'confirmed_external'>; label: string }> = [
  { key: 'opportunity', label: 'Oportunidad' },
  { key: 'proposed', label: 'Propuesta' },
  { key: 'accepted', label: 'Aceptada' },
  { key: 'confirmed_from_conversation', label: 'Confirmada' },
];

export interface AppointmentFunnelProps {
  state: FunnelState;
  /** Optional detail per step (e.g. "con horarios concretos", "sábado 11:00"). */
  details?: Partial<Record<'opportunity' | 'proposed' | 'accepted' | 'confirmed_from_conversation', React.ReactNode>>;
  className?: string;
}

function reached(state: FunnelState): number {
  if (state === 'none') return 0;
  if (state === 'confirmed_external') return 4;
  return STEPS.findIndex((s) => s.key === state) + 1;
}

/** Appointment progress with provenance always visible; only external confirmations show as verified. */
export function AppointmentFunnel({ state, details, className }: AppointmentFunnelProps) {
  const n = reached(state);
  const verified = state === 'confirmed_external';
  return (
    <div className={cn('grid gap-3', className)}>
      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Embudo de cita">
        {STEPS.map((s, i) => {
          const done = i < n;
          return (
            <li
              key={s.key}
              aria-current={i === n - 1 ? 'step' : undefined}
              className={cn(
                'grid gap-0.5 rounded-menu border px-3 py-2',
                done ? 'border-brand-primary/30 bg-brand-tint' : 'border-border-subtle bg-surface-subtle',
              )}
            >
              <span className={cn('inline-flex items-center gap-1.5 text-helper font-medium', done ? 'text-brand-hover' : 'text-text-muted')}>
                {done ? <Check aria-hidden className="size-3.5" /> : <CircleDashed aria-hidden className="size-3.5" />}
                {s.label}
                <span className="sr-only">{done ? ' (alcanzado)' : ' (no alcanzado)'}</span>
              </span>
              {details?.[s.key] ? <span className="text-helper text-text-secondary">{details[s.key]}</span> : null}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        {n === 0 ? <span className="text-helper text-text-secondary">Sin oportunidad de cita detectada.</span> : null}
        {n > 0 ? <ProvenanceBadge verified={verified} /> : null}
      </div>
    </div>
  );
}

// ───────── SignalChips ─────────

export interface SignalChip {
  key: string;
  label: string;
  /** High-intent signal (§11): emphasized with an icon and "alta intención" text, not color only. */
  high?: boolean;
}

export interface SignalChipsProps {
  levelLabel: string;
  level: 'high' | 'medium' | 'low' | 'unknown';
  signals: readonly SignalChip[];
  className?: string;
}

export function SignalChips({ levelLabel, level, signals, className }: SignalChipsProps) {
  return (
    <div className={cn('grid gap-2', className)}>
      <p className="inline-flex items-center gap-1.5 text-body font-medium text-text-primary">
        {level === 'high' ? <Flame aria-hidden className="size-4 text-danger" /> : <Sparkles aria-hidden className="size-4 text-text-muted" />}
        Intención {levelLabel.toLowerCase()}
      </p>
      {signals.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Señales de intención">
          {signals.map((s) => (
            <li
              key={s.key}
              className={cn(
                'inline-flex items-center gap-1 rounded-pill border px-2.5 py-0.5 text-helper',
                s.high ? 'border-danger/25 bg-danger-bg font-medium text-danger' : 'border-border-subtle bg-surface-subtle text-text-secondary',
              )}
            >
              {s.high ? <Flame aria-hidden className="size-3.5" /> : null}
              {s.label}
              {s.high ? <span className="sr-only"> (alta intención)</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-helper text-text-secondary">Sin señales de intención claras.</p>
      )}
    </div>
  );
}

// ───────── FindingLedger ─────────

export type LedgerTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE: Record<LedgerTone, string> = {
  success: 'border-success/25 bg-success-bg text-success',
  warning: 'border-warning/25 bg-warning-bg text-warning',
  danger: 'border-danger/25 bg-danger-bg text-danger',
  info: 'border-info/25 bg-info-bg text-info',
  neutral: 'border-border-subtle bg-surface-subtle text-text-secondary',
};

export interface FindingLedgerItem {
  id: string;
  title: React.ReactNode;
  status: { tone: LedgerTone; label: string };
  detail?: React.ReactNode;
  evidence?: React.ReactNode;
}

export interface FindingLedgerProps {
  /** Accessible name of the list. */
  label: string;
  items: readonly FindingLedgerItem[];
  emptyText?: string;
  className?: string;
}

/** Compact ledger of findings (questions, objections, pending actions, follow-up quality). */
export function FindingLedger({ label, items, emptyText = 'Nada que mostrar.', className }: FindingLedgerProps) {
  if (items.length === 0) return <p className={cn('text-helper text-text-secondary', className)}>{emptyText}</p>;
  return (
    <ul aria-label={label} className={cn('grid divide-y divide-border-subtle rounded-menu border border-border-subtle', className)}>
      {items.map((it) => (
        <li key={it.id} className="grid gap-1 px-3 py-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <span className="min-w-0 text-body text-text-primary">{it.title}</span>
            <span className={cn('inline-flex whitespace-nowrap rounded-pill border px-2 py-0.5 text-helper font-medium', TONE[it.status.tone])}>{it.status.label}</span>
          </div>
          {it.detail ? <div className="text-helper text-text-secondary">{it.detail}</div> : null}
          {it.evidence ? <div className="flex flex-wrap gap-1.5">{it.evidence}</div> : null}
        </li>
      ))}
    </ul>
  );
}
